/**
 * Date, ore e numeri — sempre in italiano e sempre in chiaro.
 *
 * La regola che governa questo file: un operatore non deve mai dover calcolare
 * niente. «Scade fra 24 ore» costringe a fare una sottrazione alle tre di
 * notte; «scade giovedì alle 20:00» no. E scrivere l'esito per esteso è anche
 * l'unico modo in cui un fuso orario sbagliato sul computer del COC si vede
 * prima di pubblicare, invece che dopo.
 */

const TZ = 'Europe/Rome';

const dt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('it-IT', { timeZone: TZ, ...opts });

// `useGrouping: 'always'` e non il default: in italiano CLDR chiede almeno due
// cifre prima del gruppo, quindi 4212 resterebbe «4212» — che si legge come un
// codice, non come una quantità. Su una schermata che esiste per far atterrare
// quel numero, «4.212» è tutta la differenza.
const numbers = new Intl.NumberFormat('it-IT', { useGrouping: 'always' });

export const formatNumber = (n: number): string => numbers.format(n);

/** «giovedì 14 agosto alle 20:00» */
export function formatFull(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = dt({ weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  const time = dt({ hour: '2-digit', minute: '2-digit' }).format(d);
  return `${day} alle ${time}`;
}

/** «oggi alle 20:00», «domani alle 8:00», altrimenti la forma per esteso. */
export function formatRelativeDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  const dayKey = (x: Date) => dt({ year: 'numeric', month: '2-digit', day: '2-digit' }).format(x);
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86_400_000);
  const time = dt({ hour: '2-digit', minute: '2-digit' }).format(d);

  if (dayKey(d) === dayKey(now)) return `oggi alle ${time}`;
  if (dayKey(d) === dayKey(tomorrow)) return `domani alle ${time}`;
  return formatFull(iso);
}

/** «da 26 ore», «da 40 minuti» — per il sollecito di revisione. */
export function formatAge(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `da ${minutes} minut${minutes === 1 ? 'o' : 'i'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `da ${hours} or${hours === 1 ? 'a' : 'e'}`;
  return `da ${Math.floor(hours / 24)} giorni`;
}

/**
 * `<input type="datetime-local">` produce «2026-08-14T20:00» senza fuso, e il
 * browser lo intende nell'ora locale della macchina. Lo trasformiamo in ISO
 * con offset qui, in un posto solo: è l'unico punto in cui l'ambiguità esiste.
 */
export function toIsoFromLocalInput(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** L'inverso, per riempire un `datetime-local` da un ISO. */
export function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** ISO di «fra N ore da adesso». */
export function isoInHours(hours: number): string {
  return new Date(Date.now() + hours * 3_600_000).toISOString();
}

/**
 * ISO della prossima occorrenza di un'ora del giorno (ora locale italiana).
 * Serve ai preset «stasera alle 20:00» e «domani alle 8:00»: se le 20:00 sono
 * già passate, il preset punta a domani, che è sempre ciò che si intendeva.
 */
export function isoAtNextHour(hour: number): string {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

/**
 * I codici d'errore di FCM in italiano.
 *
 * Il codice grezzo non dice niente a un comune; la causa sì, e cambia cosa
 * pensare del numero: mille «app disinstallata» sono normali, mille
 * «token non valido» sono un problema nostro.
 */
export function explainErrorCode(code: string): string {
  const map: Record<string, string> = {
    UNREGISTERED: 'app disinstallata o notifiche disattivate',
    NOT_FOUND: 'app disinstallata o notifiche disattivate',
    INVALID_ARGUMENT: 'token non valido (problema nostro)',
    SENDER_ID_MISMATCH: 'token di un altro progetto (problema nostro)',
    QUOTA_EXCEEDED: 'limite di invio raggiunto, riprovato più tardi',
    UNAVAILABLE: 'servizio di notifica momentaneamente irraggiungibile',
    INTERNAL: 'errore del servizio di notifica',
    THIRD_PARTY_AUTH_ERROR: 'certificato Apple non valido (problema nostro)',
    sconosciuto: 'motivo non registrato',
  };
  return map[code] ?? code;
}
